import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Settings } from "../actions/apiActions";
import { useSystemId, useSystemConfig } from "./useReduxSelectors";
import { enqueueSnackbar } from "notistack";

/** Convert string numeric values to numbers in an object/array tree. Commas are treated as decimal separators. */
const parseNumericValues = (data) => {
  if (Array.isArray(data)) return data.map(parseNumericValues);
  if (data && typeof data === "object") {
    const result = {};
    for (const [key, val] of Object.entries(data)) {
      result[key] = parseNumericValues(val);
    }
    return result;
  }
  if (typeof data === "string" && data !== "") {
    const normalized = data.replace(",", ".");
    const num = Number(normalized);
    return isNaN(num) ? data : num;
  }
  return data;
};

/**
 * Hook to fetch and save settings with local state management.
 * @param {string} settingTableName - The SETTING_TABLE_NAME constant
 * @param {function} [transformResponse] - Optional transform applied to response.jsonData before setting state
 * @returns {{ formState, setFormState, loading, error, fetchSettings, saveSettings, updateField, updateComponentField }}
 */
const useSettings = (settingTableName, transformResponse) => {
  const { t } = useTranslation("common");
  const systemId = useSystemId();
  const config = useSystemConfig();

  const [formState, setFormState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchSettings = useCallback(async () => {
    if (!config) return;
    setLoading(true);
    setError("");
    try {
      const response = await Settings.get(config.SETTING_URL, {
        customerId: config.CUSTOMER_ID,
        deviceId: config.DEVICE_ID,
        setting: settingTableName,
      });
      const parsed = { ...response, jsonData: JSON.parse(response.jsonData) };
      const data = transformResponse ? transformResponse(parsed) : parsed.jsonData;
      setFormState(data);
    } catch (err) {
      setError(err.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [config, settingTableName, transformResponse]);

  const saveSettings = useCallback(async (payload) => {
    if (!config) return;
    try {
      const parsed = parseNumericValues(payload);
      await Settings.post(config.SETTING_URL, {
        customerId: config.CUSTOMER_ID,
        deviceId: config.DEVICE_ID,
        setting: settingTableName,
        jsonData: JSON.stringify(parsed),
      });
      enqueueSnackbar(t("Save Success"), { variant: "success" });
      await fetchSettings();
    } catch (err) {
      enqueueSnackbar(t("Failed to save"), { variant: "error" });
    }
  }, [config, settingTableName, fetchSettings, t]);

  // Simple field update: setFormState(prev => ({ ...prev, [key]: value }))
  const updateField = useCallback((key, value) => {
    setFormState((prev) => (prev ? { ...prev, [key]: value } : prev));
  }, []);

  // Update a field within a named component in a components array
  const updateComponentField = useCallback((componentName, field, value) => {
    setFormState((prev) => {
      if (!prev?.components) return prev;
      return {
        ...prev,
        components: prev.components.map((c) =>
          c.name === componentName ? { ...c, [field]: value } : c
        ),
      };
    });
  }, []);

  return { formState, setFormState, loading, error, fetchSettings, saveSettings, updateField, updateComponentField };
};

export default useSettings;
